import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import { Button } from '../components/ui/Button';
import {
  RefreshCw,
  Download,
  Upload,
  AlertCircle,
  Check,
  X,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Settings,
  Zap,
  Plus,
  Package,
  Image as ImageIcon,
  ScanSearch,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  ImportPreviewModal,
  type AmbiguousMatch,
  type PreviewProduct,
} from '../components/shopify/ImportPreviewModal';
import { MappingConflicts } from '../components/shopify/MappingConflicts';
import { SyncStatusDashboard } from '../components/shopify/SyncStatusDashboard';
import { SkuMappingPanel } from '../components/shopify/SkuMappingPanel';
import { ImageRetryPanel } from '../components/shopify/ImageRetryPanel';
import { isAbortError } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';

interface SyncSettings {
  auto_sync_enabled: boolean;
  inventory_pull_enabled: boolean;
  last_pull_at: string | null;
  last_push_at: string | null;
}

interface PullResult {
  success: boolean;
  updated_in_supabase: number;
  updated_by_shopify_id: number;
  prices_synced: number;
  cost_prices_synced: number;
  locations_synced: number;
  suppliers_synced: number;
  missing_in_supabase: string[];
  duplicates_in_supabase: Array<{ sku: string; product_ids: string[] }>;
  shopify_products_scanned: number;
  shopify_variants_processed: number;
  with_sku_count: number;
  without_sku_count: number;
  draft_skipped_count: number;
  /** Chunked pull: more Shopify pages remain */
  has_more?: boolean;
  next_shopify_cursor?: string | null;
  error?: string;
}

function mergePullResults(a: PullResult, b: PullResult): PullResult {
  const missing = [...new Set([...a.missing_in_supabase, ...b.missing_in_supabase])];
  const dupMap = new Map<string, Set<string>>();
  for (const d of [...a.duplicates_in_supabase, ...b.duplicates_in_supabase]) {
    const set = dupMap.get(d.sku) ?? new Set<string>();
    for (const id of d.product_ids) set.add(id);
    dupMap.set(d.sku, set);
  }
  const duplicates_in_supabase = [...dupMap.entries()].map(([sku, ids]) => ({
    sku,
    product_ids: [...ids],
  }));
  return {
    success: a.success && b.success,
    updated_in_supabase: a.updated_in_supabase + b.updated_in_supabase,
    updated_by_shopify_id: a.updated_by_shopify_id + b.updated_by_shopify_id,
    prices_synced: a.prices_synced + b.prices_synced,
    cost_prices_synced: a.cost_prices_synced + b.cost_prices_synced,
    locations_synced: a.locations_synced + b.locations_synced,
    suppliers_synced: a.suppliers_synced + b.suppliers_synced,
    missing_in_supabase: missing,
    duplicates_in_supabase,
    shopify_products_scanned: a.shopify_products_scanned + b.shopify_products_scanned,
    shopify_variants_processed: a.shopify_variants_processed + b.shopify_variants_processed,
    with_sku_count: a.with_sku_count + b.with_sku_count,
    without_sku_count: a.without_sku_count + b.without_sku_count,
    draft_skipped_count: a.draft_skipped_count + b.draft_skipped_count,
    has_more: b.has_more,
    next_shopify_cursor: b.next_shopify_cursor,
    error: b.error ?? a.error,
  };
}

interface VerificationResult {
  sampled: number;
  matched: number;
  mismatched: number;
  fixed: number;
  mismatches: Array<{ sku: string; expected: number; actual: number | null; fixed: boolean }>;
  skipped: boolean;
}

interface PushResult {
  success: boolean;
  updated_in_shopify: number;
  updated_by_shopify_id: number;
  missing_in_shopify: string[];
  skipped_no_location: string[];
  errors: Array<{ sku: string; error: string }>;
  mode: string;
  with_sku_count: number;
  without_sku_count: number;
  verification?: VerificationResult;
}

interface PreviewResult {
  success: boolean;
  /** The store the preview was read from; echoed back on import so the server can
   *  refuse a payload previewed under a different company. Optional because an
   *  older deployment of the preview function does not return it. */
  shop_domain?: string;
  total_shopify_products: number;
  total_shopify_variants: number;
  missing_products: PreviewProduct[];
  existing_count: number;
  with_sku_count: number;
  without_sku_count: number;
  draft_skipped_count: number;
  /** Counted inside existing_count. Optional because an older deployment of the
   *  preview function does not return these. */
  ambiguous_count?: number;
  /** A capped sample; `ambiguous_count` is the total. */
  ambiguous_matches?: AmbiguousMatch[];
  progress?: {
    phase: 'products' | 'inventory' | 'complete';
    loaded: number;
    total: number;
  };
  error?: string;
}

interface ImportResult {
  success: boolean;
  created: number;
  skipped: number;
  linked: number;
  /** Counted inside skipped, and one entry per item is also added to errors.
   *  Optional because an older deployment does not return it. */
  ambiguous?: number;
  colors_created: number;
  sizes_created: number;
  errors: Array<{ sku: string; error: string }>;
}

interface ImageSyncResult {
  success: boolean;
  updated: number;
  no_image: number;
  no_match: number;
  errors: number;
  error_details: string[];
  scanned: number;
  draft_skipped: number;
  total_products: number;
  matched_by_sku: number;
  matched_by_variant_gid: number;
  matched_by_product_gid: number;
  matched_by_map: number;
  variant_sourced: number;
  product_fallback: number;
  no_image_skus: string[];
  error?: string;
}

interface DetectImageSyncResult extends ImageSyncResult {
  already_has_image: number;
  matched_by_sku_map: number;
  matched_by_reverse: number;
  broken_url_fixed: number;
  never_encountered: number;
}

export const ShopifySyncSettings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const adminId = useMemo(() => {
    if (!user) return null;
    return getAdminId(user);
  }, [user, isAdmin]);

  const abortRef = useRef<AbortController | null>(null);

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<SyncSettings | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [pulling, setPulling] = useState(false);
  const [pullStatus, setPullStatus] = useState<string | null>(null);
  const [pullResult, setPullResult] = useState<PullResult | null>(null);
  const [showPullDetails, setShowPullDetails] = useState(false);

  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState<PushResult | null>(null);
  const [showPushDetails, setShowPushDetails] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [pushStatus, setPushStatus] = useState<string | null>(null);

  const [updatingAutoSync, setUpdatingAutoSync] = useState(false);
  const [updatingInventoryPull, setUpdatingInventoryPull] = useState(false);

  const [showImportModal, setShowImportModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewProducts, setPreviewProducts] = useState<PreviewProduct[]>([]);
  const [previewShopDomain, setPreviewShopDomain] = useState<string | null>(null);
  const [previewStats, setPreviewStats] = useState<{
    total: number;
    existing: number;
    with_sku: number;
    without_sku: number;
    draft_skipped: number;
    ambiguous: number;
  } | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<{
    phase: 'products' | 'inventory' | 'complete';
    loaded: number;
    total: number;
  } | null>(null);
  const [ambiguousMatches, setAmbiguousMatches] = useState<AmbiguousMatch[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  const [existingLocations, setExistingLocations] = useState<string[]>([]);

  const [syncingImages, setSyncingImages] = useState(false);
  const [imageSyncResult, setImageSyncResult] = useState<ImageSyncResult | null>(null);
  const [showImageSyncDetails, setShowImageSyncDetails] = useState(false);
  const [showImageSyncConfirm, setShowImageSyncConfirm] = useState(false);

  const [detectingImages, setDetectingImages] = useState(false);
  const [detectImageResult, setDetectImageResult] = useState<DetectImageSyncResult | null>(null);
  const [showDetectImageDetails, setShowDetectImageDetails] = useState(false);

  const [imageJobId, setImageJobId] = useState<string | null>(null);
  const [imageJobProgress, setImageJobProgress] = useState<{
    syncedCount: number;
    totalEstimate: number;
    stats: Record<string, number | string[] | unknown>;
    startedAt: number;
  } | null>(null);

  const [reversingImages, setReversingImages] = useState(false);
  const [reverseJobId, setReverseJobId] = useState<string | null>(null);
  const [reverseProgress, setReverseProgress] = useState<{
    syncedCount: number;
    totalEstimate: number;
    stats: Record<string, number | string[] | unknown>;
  } | null>(null);
  const [reverseResult, setReverseResult] = useState<Record<string, unknown> | null>(null);

  const [resettingStuckSync, setResettingStuckSync] = useState(false);

  const detectingRef = useRef(false);
  const syncingRef = useRef(false);
  const reversingRef = useRef(false);

  const fetchStatus = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-oauth/status`,
        {
          headers: await getValidAuthHeadersOrThrow(),
          signal: signal ?? AbortSignal.timeout(30000),
        }
      );
      if (!response.ok) {
        throw new Error(`Status check failed: ${response.status}`);
      }
      const data = await response.json();
      setConnected(data.connected && data.shops?.length > 0);
    } catch (err) {
      if (isAbortError(err)) return;
      setConnected(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!adminId) return;

    const { data, error: fetchError } = await supabase
      .from('shopify_settings')
      .select('auto_sync_enabled, inventory_pull_enabled, last_pull_at, last_push_at')
      .eq('admin_id', adminId)
      .maybeSingle();

    if (fetchError) {
      setError(`Failed to load sync settings: ${fetchError.message}`);
    } else {
      setSettings(data || {
        auto_sync_enabled: false,
        inventory_pull_enabled: false,
        last_pull_at: null,
        last_push_at: null,
      });
    }
    setLoading(false);
  }, [adminId]);

  const fetchCategoriesAndLocations = useCallback(async () => {
    if (!adminId) return;

    const { data: products, error: catErr } = await supabase
      .from('products')
      .select('category, location')
      .eq('admin_id', adminId);

    if (catErr) {
      setError(`Failed to load categories: ${catErr.message}`);
      return;
    }

    if (products) {
      const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
      const locations = [...new Set(products.map(p => p.location).filter(Boolean))];
      setExistingCategories(categories.sort());
      setExistingLocations(locations.sort());
    }
  }, [adminId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchStatus(controller.signal);
    fetchSettings();
    fetchCategoriesAndLocations();
    return () => {
      controller.abort();
      abortRef.current?.abort();
    };
  }, [fetchStatus, fetchSettings, fetchCategoriesAndLocations]);

  const handlePreviewImport = async () => {
    setPreviewLoading(true);
    setError(null);
    setImportResult(null);
    setPreviewProducts([]);
    setPreviewShopDomain(null);
    setPreviewStats(null);
    setAmbiguousMatches([]);
    setLoadingProgress({ phase: 'products', loaded: 0, total: 0 });
    setShowImportModal(true);

    let progressInterval: ReturnType<typeof setInterval> | null = null;
    let estimatedProducts = 100;
    let currentLoaded = 0;

    progressInterval = setInterval(() => {
      currentLoaded = Math.min(currentLoaded + Math.floor(Math.random() * 5) + 2, estimatedProducts - 10);
      setLoadingProgress(prev => {
        if (!prev || prev.phase === 'complete') return prev;
        return {
          ...prev,
          loaded: currentLoaded,
          total: estimatedProducts,
        };
      });
    }, 500);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-preview-import`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
        }
      );

      const data: PreviewResult = await response.json();

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to preview products from Shopify');
      }

      if (data.progress) {
        setLoadingProgress({
          phase: 'complete',
          loaded: data.progress.total,
          total: data.progress.total,
        });
      }

      setPreviewProducts(data.missing_products);
      setPreviewShopDomain(data.shop_domain ?? null);
      setPreviewStats({
        total: data.total_shopify_variants,
        existing: data.existing_count,
        with_sku: data.with_sku_count,
        without_sku: data.without_sku_count,
        draft_skipped: data.draft_skipped_count,
        ambiguous: data.ambiguous_count ?? 0,
      });
      setAmbiguousMatches(data.ambiguous_matches ?? []);
    } catch (err) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setShowImportModal(false);
      setError(err instanceof Error ? err.message : 'Failed to preview products');
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setPreviewLoading(false);
      setLoadingProgress(null);
    }
  };

  const handleImportProducts = async (
    selectedProducts: PreviewProduct[],
    settings: { default_category: string; default_location: string; default_min_stock: number }
  ) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-import-products`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({
            products: selectedProducts,
            settings,
            // Lets the server refuse the import if the active company changed
            // between previewing and importing, which would otherwise create this
            // store's products under whichever company is now active.
            shop_domain: previewShopDomain,
          }),
        }
      );

      const data: ImportResult = await response.json();

      if (!response.ok || !data.success) {
        throw new Error((data as any).error || 'Failed to import products');
      }

      setImportResult(data);
      setShowImportModal(false);

      // Ambiguous items are reported as errors too, so they must not also be
      // counted as failures or the summary double-reports them.
      const ambiguous = data.ambiguous ?? 0;
      const failed = Math.max(0, data.errors.length - ambiguous);

      let message = `Successfully imported ${data.created} products`;
      if (data.linked > 0) {
        message += ` | ${data.linked} linked to Shopify`;
      }
      if (data.colors_created > 0 || data.sizes_created > 0) {
        message += ` (${data.colors_created} colors, ${data.sizes_created} sizes created)`;
      }
      // `ambiguous` is a subset of `skipped`, so it is reported as a breakdown of
      // it rather than as another term the reader would add on.
      const skippedCleanly = data.skipped - ambiguous;
      if (skippedCleanly > 0) {
        message += ` | ${skippedCleanly} skipped (already exist)`;
      }
      if (ambiguous > 0) {
        message += ` | ${ambiguous} skipped needing cleanup, see the list below`;
      }
      if (failed > 0) {
        message += ` | ${failed} failed`;
      }

      if (data.created === 0 && data.skipped > 0 && data.linked === 0) {
        const parts: string[] = [];
        if (ambiguous > 0) {
          parts.push(
            `${ambiguous} ${ambiguous === 1 ? 'variant' : 'variants'} could not be matched to a single product, so ${ambiguous === 1 ? 'it was' : 'they were'} not imported or linked. See the list below for what to fix.`
          );
        }
        if (skippedCleanly > 0) {
          parts.push(
            `${skippedCleanly} already ${skippedCleanly === 1 ? 'exists' : 'exist'} in your inventory but could not be linked. Check Shopify tracking settings.`
          );
        }
        setError(parts.join(' '));
      } else {
        setSuccess(message);
      }

      fetchCategoriesAndLocations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import products');
      throw err;
    }
  };

  const handlePull = async () => {
    setPulling(true);
    setPullStatus(null);
    setError(null);
    setPullResult(null);

    const MAX_CHUNK_RETRIES = 5;
    let merged: PullResult | null = null;
    let cursor: string | null = null;
    let chunkIndex = 0;
    let done = false;

    try {
      while (!done) {
        chunkIndex += 1;
        setPullStatus(
          chunkIndex === 1
            ? 'Syncing from Shopify…'
            : `Syncing from Shopify… (continuing, part ${chunkIndex})`
        );

        let chunkData: PullResult | undefined;
        for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-pull-products`,
            {
              method: 'POST',
              headers: await getValidAuthHeadersOrThrow(),
              body: JSON.stringify({ shopify_cursor: cursor }),
            }
          );

          let data: PullResult;
          try {
            data = (await response.json()) as PullResult;
          } catch {
            if (response.status === 504) {
              throw new Error('Request timed out. Your store may have many products. Please try again.');
            }
            const isTransient =
              response.status === 502 ||
              response.status === 503 ||
              response.status === 546;
            if (isTransient && attempt < MAX_CHUNK_RETRIES) {
              await new Promise(r => setTimeout(r, 8000 * Math.pow(2, attempt - 1)));
              continue;
            }
            throw new Error(`Server returned ${response.status}. Please try again.`);
          }

          if (response.ok && data.success) {
            chunkData = data;
            break;
          }

          const errMsg = data.error || 'Failed to pull from Shopify';
          const isTransient =
            response.status === 502 ||
            response.status === 503 ||
            response.status === 546;
          if (isTransient && attempt < MAX_CHUNK_RETRIES) {
            await new Promise(r => setTimeout(r, 8000 * Math.pow(2, attempt - 1)));
            continue;
          }
          throw new Error(errMsg);
        }

        if (!chunkData) {
          throw new Error('Failed to pull from Shopify');
        }

        merged = merged ? mergePullResults(merged, chunkData) : chunkData;
        setPullResult(merged);

        if (!chunkData.has_more) {
          done = true;
          break;
        }
        const nextCursor = chunkData.next_shopify_cursor;
        if (!nextCursor) {
          throw new Error('Sync incomplete: missing next page cursor. Try again.');
        }
        cursor = nextCursor;
      }

      setPullStatus(null);
      if (merged) {
        const totalUpdated = merged.updated_in_supabase + merged.updated_by_shopify_id;
        setSuccess(`Successfully updated ${totalUpdated} products from Shopify`);
      }
      fetchSettings();
    } catch (err) {
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Request timed out. The server may still be processing. Try refreshing in a minute.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to pull from Shopify');
      }
    } finally {
      setPulling(false);
      setPullStatus(null);
    }
  };

  const handlePush = async (mode: 'changed' | 'all') => {
    setPushing(true);
    setError(null);
    setPushResult(null);
    setShowPushConfirm(false);
    setPushStatus(null);

    const MAX_PASSES = 3;
    let lastData: PushResult | null = null;

    try {
      for (let pass = 1; pass <= MAX_PASSES; pass++) {
        setPushStatus(pass === 1
          ? `Pushing to Shopify... (pass ${pass} of ${MAX_PASSES})`
          : `Retrying mismatches... (pass ${pass} of ${MAX_PASSES})`
        );

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-push-products`,
          {
            method: 'POST',
            headers: await getValidAuthHeadersOrThrow(),
            body: JSON.stringify({ mode }),
            signal: AbortSignal.timeout(180000),
          }
        );

        let data: PushResult;
        try {
          data = await response.json();
        } catch {
          if (response.status === 504) {
            throw new Error('Request timed out. Your store may have many products. Please try again.');
          }
          throw new Error(`Server returned ${response.status}. Please try again.`);
        }

        if (!response.ok) {
          throw new Error((data as any).error || 'Failed to push to Shopify');
        }

        lastData = data;
        setPushResult(data);

        const unfixedMismatches = data.verification?.mismatches?.filter(m => !m.fixed).length ?? 0;
        if (unfixedMismatches === 0 || data.verification?.skipped) {
          break;
        }

        if (pass < MAX_PASSES) {
          setPushStatus(`Found ${unfixedMismatches} unresolved mismatch(es), retrying in 2s...`);
          await new Promise(r => setTimeout(r, 2000));
        }
      }

      setPushStatus(null);

      if (lastData) {
        const totalPushed = lastData.updated_in_shopify + lastData.updated_by_shopify_id;
        const unfixedMismatches = lastData.verification?.mismatches?.filter(m => !m.fixed).length ?? 0;

        if (lastData.success && unfixedMismatches === 0) {
          if (lastData.verification && !lastData.verification.skipped) {
            setSuccess(`Successfully pushed ${totalPushed} products to Shopify (${lastData.verification.matched}/${lastData.verification.sampled} verified, ${lastData.verification.fixed} fixed)`);
          } else {
            setSuccess(`Successfully pushed ${totalPushed} products to Shopify`);
          }
        } else if (totalPushed > 0) {
          const parts = [`Pushed ${totalPushed} products`];
          if (unfixedMismatches > 0) parts.push(`${unfixedMismatches} mismatch(es) remain`);
          if (lastData.errors.length > 0) parts.push(`${lastData.errors.length} error(s)`);
          if (lastData.verification?.fixed) parts.push(`${lastData.verification.fixed} auto-fixed`);
          setSuccess(parts.join(' — ') + '. Check details.');
        } else {
          throw new Error(lastData.errors?.[0]?.error || 'Failed to push to Shopify');
        }
        fetchSettings();
      }
    } catch (err) {
      setPushStatus(null);
      if (err instanceof TypeError && err.message === 'Failed to fetch') {
        setError('Request timed out. The server may still be processing. Try refreshing in a minute.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to push to Shopify');
      }
    } finally {
      setPushing(false);
      setPushStatus(null);
    }
  };

  const handleToggleAutoSync = async () => {
    if (!adminId) return;

    setUpdatingAutoSync(true);

    try {
      const newValue = !settings?.auto_sync_enabled;

      const { error: updateError } = await supabase
        .from('shopify_settings')
        .upsert({
          admin_id: adminId,
          auto_sync_enabled: newValue,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'admin_id' });

      if (updateError) throw updateError;

      setSettings(prev => prev ? { ...prev, auto_sync_enabled: newValue } : null);
      setSuccess(newValue ? 'Auto-sync enabled' : 'Auto-sync disabled');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update auto-sync setting');
    } finally {
      setUpdatingAutoSync(false);
    }
  };

  const handleToggleInventoryPull = async () => {
    if (!adminId) return;

    setUpdatingInventoryPull(true);

    try {
      const newValue = !settings?.inventory_pull_enabled;

      const { error: updateError } = await supabase
        .from('shopify_settings')
        .upsert({
          admin_id: adminId,
          inventory_pull_enabled: newValue,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'admin_id' });

      if (updateError) throw updateError;

      setSettings(prev => prev ? { ...prev, inventory_pull_enabled: newValue } : null);
      setSuccess(
        newValue
          ? 'Shopify inventory webhook pull enabled'
          : 'Shopify inventory webhook pull disabled'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update inventory pull setting');
    } finally {
      setUpdatingInventoryPull(false);
    }
  };

  const runImageSyncJob = async (mode: 'detect' | 'replace') => {
    const isDetect = mode === 'detect';
    const guardRef = isDetect ? detectingRef : syncingRef;
    if (guardRef.current) return;
    guardRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    if (isDetect) {
      setDetectingImages(true);
      setDetectImageResult(null);
    } else {
      setSyncingImages(true);
      setImageSyncResult(null);
      setShowImageSyncConfirm(false);
    }
    setError(null);
    setSuccess(null);

    let retries = 0;
    const MAX_RETRIES = 12;
    let currentJobId: string | null = null;

    try {
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await getValidAuthHeadersOrThrow();

      const startRes = await fetch(`${baseUrl}/functions/v1/shopify-start-image-sync`, {
        method: 'POST',
        headers,
        signal: controller.signal,
        body: JSON.stringify({ mode }),
      });

      if (!startRes.ok) {
        const errData = await startRes.json().catch(() => ({ error: `Server error: ${startRes.status}` }));
        throw new Error(errData.error || `Failed to start image sync`);
      }

      const startData = await startRes.json();
      currentJobId = startData.jobId;
      setImageJobId(currentJobId);
      setImageJobProgress({
        syncedCount: startData.syncedCount || 0,
        totalEstimate: startData.totalEstimate || 0,
        stats: {},
        startedAt: Date.now(),
      });

      while (true) {
        if (controller.signal.aborted) throw new Error('Cancelled');

        try {
          const chunkRes = await fetch(
            `${baseUrl}/functions/v1/shopify-image-sync-chunk?jobId=${currentJobId}`,
            { method: 'POST', headers, signal: controller.signal }
          );

          if (!chunkRes.ok) {
            const errText = await chunkRes.text().catch(() => '');
            // CDN-URL mode does no image processing, so WORKER_LIMIT (546)
            // should not occur. We still treat 502/503/546 as transient and
            // retry with backoff so a brief edge/gateway blip resumes from the
            // last saved cursor instead of failing the whole job.
            const isTransientError =
              chunkRes.status === 502 ||
              chunkRes.status === 503 ||
              chunkRes.status === 546 ||
              errText.includes('WORKER_LIMIT') ||
              errText.includes('compute resources');

            if (isTransientError) {
              retries++;
              if (retries >= MAX_RETRIES) {
                throw new Error(
                  'Server was briefly unavailable. Progress is saved — run Replace All Images again to resume where it left off.'
                );
              }
              await new Promise(r => setTimeout(r, 2000 * retries));
              continue;
            }

            let errorMsg = `Server error: ${chunkRes.status}`;
            try { const errJson = JSON.parse(errText); if (errJson.error) errorMsg = errJson.error; } catch { /* use default */ }
            throw new Error(errorMsg);
          }

          const data = await chunkRes.json();
          retries = 0;

          setImageJobProgress(prev => ({
            syncedCount: data.syncedCount || 0,
            totalEstimate: data.totalEstimate || prev?.totalEstimate || 0,
            stats: data.stats || {},
            startedAt: prev?.startedAt || Date.now(),
          }));

          if (!data.hasMore) {
            const stats = data.stats || {};
            if (isDetect) {
              setDetectImageResult({
                success: true,
                updated: stats.updated || 0,
                no_image: stats.no_image || 0,
                no_match: stats.no_match || 0,
                errors: stats.errors || 0,
                error_details: stats.error_details || [],
                scanned: stats.scanned || 0,
                draft_skipped: stats.draft_skipped || 0,
                total_products: data.totalEstimate || 0,
                already_has_image: stats.already_has_image || 0,
                matched_by_sku: stats.matched_by_sku || 0,
                matched_by_sku_map: stats.matched_by_sku_map || 0,
                matched_by_variant_gid: stats.matched_by_variant_gid || 0,
                matched_by_product_gid: stats.matched_by_product_gid || 0,
                matched_by_map: stats.matched_by_map || 0,
                variant_sourced: stats.variant_sourced || 0,
                product_fallback: stats.product_fallback || 0,
                matched_by_reverse: 0,
                broken_url_fixed: 0,
                never_encountered: 0,
                no_image_skus: stats.no_image_skus || [],
              });
              const msg = (stats.updated || 0) > 0
                ? `Added ${stats.updated} missing images (${stats.already_has_image || 0} already had images)`
                : `All matched products already have images (${stats.already_has_image || 0} checked)`;
              setSuccess(msg);
            } else {
              setImageSyncResult({
                success: true,
                updated: stats.updated || 0,
                no_image: stats.no_image || 0,
                no_match: stats.no_match || 0,
                errors: stats.errors || 0,
                error_details: stats.error_details || [],
                scanned: stats.scanned || 0,
                draft_skipped: stats.draft_skipped || 0,
                total_products: data.totalEstimate || 0,
                matched_by_sku: stats.matched_by_sku || 0,
                matched_by_variant_gid: stats.matched_by_variant_gid || 0,
                matched_by_product_gid: stats.matched_by_product_gid || 0,
                matched_by_map: stats.matched_by_map || 0,
                variant_sourced: stats.variant_sourced || 0,
                product_fallback: stats.product_fallback || 0,
                no_image_skus: stats.no_image_skus || [],
              });
              setSuccess(
                `Replaced ${stats.updated || 0} images (${stats.variant_sourced || 0} variant-sourced, ${stats.product_fallback || 0} product-fallback, ${stats.no_image || 0} no image, ${stats.no_match || 0} unmatched)`
              );
            }
            break;
          }

          const delay = data.retryAfter
            ? data.retryAfter * 1000
            : isDetect
              ? 300
              : 600;
          await new Promise(r => setTimeout(r, delay));
        } catch (batchErr) {
          if (isAbortError(batchErr)) throw new Error('Cancelled');
          if (batchErr instanceof Error && batchErr.message === 'Cancelled') throw batchErr;
          if (batchErr instanceof TypeError && batchErr.message === 'Failed to fetch') {
            retries++;
            if (retries >= MAX_RETRIES) throw new Error('Connection lost after multiple retries.');
            await new Promise(r => setTimeout(r, 8000 * Math.pow(2, retries - 1)));
            continue;
          }
          retries++;
          if (retries >= MAX_RETRIES) throw batchErr;
          await new Promise(r => setTimeout(r, 4000 * retries));
        }
      }
    } catch (err) {
      const isCancelled = err instanceof Error && err.message === 'Cancelled';
      const partialStats = imageJobProgress?.stats as Record<string, number> | undefined;
      const partialUpdated = partialStats?.updated || 0;

      if (isCancelled && currentJobId) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-cancel-image-sync`, {
            method: 'POST',
            headers: await getValidAuthHeadersOrThrow(),
            body: JSON.stringify({ jobId: currentJobId }),
          });
        } catch { /* best-effort cancel */ }
        setSuccess(partialUpdated > 0 ? `Cancelled after syncing ${partialUpdated} images` : 'Image sync cancelled');
      } else if (partialUpdated > 0) {
        setError(`Partially synced ${partialUpdated} images. Error: ${err instanceof Error ? err.message : 'Unknown'}`);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to sync images');
      }
    } finally {
      if (isDetect) setDetectingImages(false);
      else setSyncingImages(false);
      setImageJobProgress(null);
      setImageJobId(null);
      guardRef.current = false;
      abortRef.current = null;
    }
  };

  const handleImageSync = () => runImageSyncJob('replace');
  const handleDetectImageSync = () => runImageSyncJob('detect');

  const handleCancelSync = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleResetStuckImageSync = useCallback(async () => {
    if (resettingStuckSync) return;
    if (!confirm('Reset any stuck image sync jobs for your store? Any in-progress job will be cancelled.')) return;

    setResettingStuckSync(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-cancel-image-sync`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ jobId: '*' }),
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: `Server error: ${res.status}` }));
        throw new Error(errData.error || 'Failed to reset stuck sync');
      }

      const data = await res.json();
      const count = (data?.cancelledCount as number | undefined) ?? 0;
      setSuccess(
        count > 0
          ? `Reset ${count} stuck image sync job${count === 1 ? '' : 's'}. You can start a new sync now.`
          : 'No stuck image sync jobs were found.'
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset stuck sync');
    } finally {
      setResettingStuckSync(false);
    }
  }, [resettingStuckSync]);

  const handleReverseLookup = async () => {
    if (reversingRef.current) return;
    reversingRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    setReversingImages(true);
    setReverseResult(null);
    setError(null);
    setSuccess(null);

    let retries = 0;
    const MAX_RETRIES = 5;
    let currentJobId: string | null = null;

    try {
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const headers = await getValidAuthHeadersOrThrow();

      const startRes = await fetch(
        `${baseUrl}/functions/v1/shopify-reverse-image-lookup?action=start`,
        { method: 'POST', headers, signal: controller.signal }
      );

      if (!startRes.ok) {
        const errData = await startRes.json().catch(() => ({ error: `Server error: ${startRes.status}` }));
        throw new Error(errData.error || 'Failed to start reverse lookup');
      }

      const startData = await startRes.json();
      currentJobId = startData.jobId;
      setReverseJobId(currentJobId);

      if (!startData.hasMore && startData.status === 'completed') {
        setReverseResult(startData.stats || {});
        setSuccess(startData.message || 'No unmatched products found');
        return;
      }

      setReverseProgress({
        syncedCount: startData.syncedCount || 0,
        totalEstimate: startData.totalEstimate || 0,
        stats: {},
      });

      while (true) {
        if (controller.signal.aborted) throw new Error('Cancelled');

        try {
          const chunkRes = await fetch(
            `${baseUrl}/functions/v1/shopify-reverse-image-lookup?jobId=${currentJobId}`,
            { method: 'POST', headers, signal: controller.signal }
          );

          if (!chunkRes.ok) {
            retries++;
            if (retries >= MAX_RETRIES) throw new Error('Server error after multiple retries.');
            await new Promise(r => setTimeout(r, 8000 * Math.pow(2, retries - 1)));
            continue;
          }

          const data = await chunkRes.json();
          retries = 0;

          setReverseProgress({
            syncedCount: data.syncedCount || 0,
            totalEstimate: data.totalEstimate || 0,
            stats: data.stats || {},
          });

          if (!data.hasMore) {
            const stats = data.stats || {};
            setReverseResult(stats);
            setSuccess(`Reverse lookup complete: ${stats.updated || 0} images added, ${stats.never_encountered || 0} not found on Shopify`);
            break;
          }

          await new Promise(r => setTimeout(r, 500));
        } catch (batchErr) {
          if (isAbortError(batchErr)) throw new Error('Cancelled');
          if (batchErr instanceof Error && batchErr.message === 'Cancelled') throw batchErr;
          retries++;
          if (retries >= MAX_RETRIES) throw batchErr;
          await new Promise(r => setTimeout(r, 4000 * retries));
        }
      }
    } catch (err) {
      const isCancelled = err instanceof Error && err.message === 'Cancelled';
      if (isCancelled && currentJobId) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-cancel-image-sync`, {
            method: 'POST',
            headers: await getValidAuthHeadersOrThrow(),
            body: JSON.stringify({ jobId: currentJobId }),
          });
        } catch { /* best-effort */ }
        setSuccess('Reverse lookup cancelled');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to run reverse lookup');
      }
    } finally {
      setReversingImages(false);
      setReverseProgress(null);
      setReverseJobId(null);
      reversingRef.current = false;
      abortRef.current = null;
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Only administrators can manage Shopify sync settings.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Connect Shopify First
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You need to connect your Shopify store before you can sync inventory.
          </p>
          <Link to="/shopify">
            <Button>
              <ShoppingBag className="h-4 w-4 mr-2" />
              Go to Shopify Integration
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <RefreshCw className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Inventory Sync
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Synchronize inventory between GetInv and Shopify
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-4 w-4 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-green-700 dark:text-green-300">{success}</p>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="h-4 w-4 text-green-600" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Download className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Pull from Shopify
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Update local inventory with Shopify quantities
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handlePull}
              loading={pulling}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Sync Existing
            </Button>
            <Button
              onClick={handlePreviewImport}
              loading={previewLoading}
              variant="secondary"
              className="flex-1 border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-900/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Import New
            </Button>
          </div>

          {pullStatus && (
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-3 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              {pullStatus}
            </p>
          )}

          {pullResult && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Sync Results
                </span>
                <button
                  onClick={() => setShowPullDetails(!showPullDetails)}
                  className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                >
                  {showPullDetails ? 'Hide' : 'Show'} details
                  {showPullDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Products Updated</span>
                  <p className="font-semibold text-green-600">
                    {pullResult.updated_in_supabase + pullResult.updated_by_shopify_id}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Prices Synced</span>
                  <p className="font-semibold text-blue-600">{pullResult.prices_synced || 0}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Cost Prices</span>
                  <p className="font-semibold text-emerald-600">{pullResult.cost_prices_synced || 0}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Locations</span>
                  <p className="font-semibold text-cyan-600">{pullResult.locations_synced || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mt-3">
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Suppliers</span>
                  <p className="font-semibold text-teal-600">{pullResult.suppliers_synced || 0}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Scanned</span>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{pullResult.shopify_variants_processed}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">With SKU</span>
                  <p className="font-semibold text-blue-600">{pullResult.with_sku_count}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">No SKU</span>
                  <p className="font-semibold text-amber-600">{pullResult.without_sku_count}</p>
                </div>
              </div>

              {pullResult.draft_skipped_count > 0 && (
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {pullResult.draft_skipped_count} draft product(s) skipped
                </div>
              )}

              {showPullDetails && (
                <div className="mt-4 space-y-3">
                  {pullResult.updated_by_shopify_id > 0 && (
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {pullResult.updated_by_shopify_id} product(s) matched by Shopify ID (no SKU in Shopify)
                      </p>
                    </div>
                  )}

                  {pullResult.missing_in_supabase.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                          Missing in GetInv ({pullResult.missing_in_supabase.length})
                        </p>
                        <Button
                          size="sm"
                          onClick={handlePreviewImport}
                          loading={previewLoading}
                          className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Import Missing
                        </Button>
                      </div>
                      <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                        {pullResult.missing_in_supabase.map((sku, i) => (
                          <p key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">{sku}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {pullResult.duplicates_in_supabase.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                        Duplicate SKUs ({pullResult.duplicates_in_supabase.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                        {pullResult.duplicates_in_supabase.map((dup, i) => (
                          <div key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">
                            <span className="font-medium">{dup.sku}</span>: {dup.product_ids.length} products
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {importResult && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="font-medium text-green-700 dark:text-green-300">
                  Import Complete
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <p className="text-gray-500 dark:text-gray-400 text-xs">Products Created</p>
                  <p className="font-semibold text-green-600">{importResult.created}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <p className="text-gray-500 dark:text-gray-400 text-xs">Colors Created</p>
                  <p className="font-semibold text-blue-600">{importResult.colors_created}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <p className="text-gray-500 dark:text-gray-400 text-xs">Sizes Created</p>
                  <p className="font-semibold text-blue-600">{importResult.sizes_created}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <p className="text-gray-500 dark:text-gray-400 text-xs">Skipped</p>
                  <p className="font-semibold text-gray-600">{importResult.skipped}</p>
                </div>
              </div>
              {(importResult.ambiguous ?? 0) > 0 && (
                <p className="mt-3 text-sm text-amber-700 dark:text-amber-400">
                  {importResult.ambiguous} of those could not be matched to a single product, so
                  {(importResult.ambiguous ?? 0) === 1 ? ' it was' : ' they were'} not linked. Each
                  one is listed below with what to fix.
                </p>
              )}
              {importResult.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                    Needs attention ({importResult.errors.length})
                  </p>
                  <div className="max-h-24 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-red-200 dark:border-red-800 p-2">
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="text-xs text-red-600 dark:text-red-400 py-0.5">
                        <span className="font-medium">{err.sku}</span>: {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Upload className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Push to Shopify
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Send local inventory quantities to Shopify
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => handlePush('changed')}
              loading={pushing}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Push Changed
            </Button>
            <div className="relative">
              <Button
                variant="secondary"
                onClick={() => setShowPushConfirm(true)}
                disabled={pushing}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>

              {showPushConfirm && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowPushConfirm(false)} />
                  <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 p-4">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      Push ALL products to Shopify? This will update every mapped product.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setShowPushConfirm(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handlePush('all')}
                        className="flex-1"
                      >
                        Push All
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {pushStatus && (
            <div className="mt-3 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 flex items-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-cyan-600 border-t-transparent rounded-full" />
              <span className="text-sm text-cyan-700 dark:text-cyan-300">{pushStatus}</span>
            </div>
          )}

          {pushResult && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Sync Results
                </span>
                <button
                  onClick={() => setShowPushDetails(!showPushDetails)}
                  className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                >
                  {showPushDetails ? 'Hide' : 'Show'} details
                  {showPushDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Updated</span>
                  <p className="font-semibold text-green-600">
                    {pushResult.updated_in_shopify + pushResult.updated_by_shopify_id}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Mode</span>
                  <p className="font-semibold text-gray-900 dark:text-gray-100 capitalize">{pushResult.mode}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">With SKU</span>
                  <p className="font-semibold text-blue-600">{pushResult.with_sku_count}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">By ID</span>
                  <p className="font-semibold text-amber-600">{pushResult.updated_by_shopify_id}</p>
                </div>
                <div className="bg-white dark:bg-gray-900 rounded p-2">
                  <span className="text-gray-500 dark:text-gray-400 text-xs">Verified</span>
                  {pushResult.verification ? (
                    pushResult.verification.skipped ? (
                      <p className="font-semibold text-gray-400">Skipped</p>
                    ) : pushResult.verification.mismatched > 0 ? (
                      <p className="font-semibold text-red-600">
                        {pushResult.verification.matched}/{pushResult.verification.sampled}
                      </p>
                    ) : (
                      <p className="font-semibold text-green-600">
                        {pushResult.verification.matched}/{pushResult.verification.sampled}
                      </p>
                    )
                  ) : (
                    <p className="font-semibold text-gray-400">N/A</p>
                  )}
                </div>
              </div>

              {showPushDetails && (
                <div className="mt-4 space-y-3">
                  {pushResult.updated_by_shopify_id > 0 && (
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {pushResult.updated_by_shopify_id} product(s) pushed using Shopify ID (no SKU match)
                      </p>
                    </div>
                  )}

                  {pushResult.skipped_no_location?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-orange-600 dark:text-orange-400 mb-1">
                        Skipped — No Location Mapping ({pushResult.skipped_no_location.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                        {pushResult.skipped_no_location.map((sku, i) => (
                          <p key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">{sku}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {pushResult.missing_in_shopify.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                        Missing in Shopify ({pushResult.missing_in_shopify.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                        {pushResult.missing_in_shopify.map((sku, i) => (
                          <p key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">{sku}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {pushResult.errors.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                        Errors ({pushResult.errors.length})
                      </p>
                      <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                        {pushResult.errors.map((err, i) => (
                          <div key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">
                            <span className="font-medium">{err.sku}</span>: {err.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pushResult.verification && !pushResult.verification.skipped && (
                    <div className={`p-2 rounded border ${
                      pushResult.verification.mismatches.some(m => !m.fixed)
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    }`}>
                      <p className={`text-xs font-medium mb-1 ${
                        pushResult.verification.mismatches.some(m => !m.fixed)
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-green-700 dark:text-green-300'
                      }`}>
                        Verification: {pushResult.verification.matched}/{pushResult.verification.sampled} items match Shopify
                        {pushResult.verification.fixed > 0 && `, ${pushResult.verification.fixed} auto-fixed`}
                        {pushResult.verification.mismatches.some(m => !m.fixed) && ` (${pushResult.verification.mismatches.filter(m => !m.fixed).length} unresolved)`}
                      </p>
                      {pushResult.verification.mismatches.length > 0 && (
                        <div className="max-h-32 overflow-y-auto mt-1">
                          {pushResult.verification.mismatches.map((m, i) => (
                            <div key={i} className={`text-xs py-0.5 ${m.fixed ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                              <span className="font-medium">{m.sku}</span>: pushed {m.expected}, Shopify had {m.actual ?? 'N/A'}{m.fixed ? ' (fixed)' : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mb-6 card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
              <ImageIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Update Images
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Download product images from Shopify matched by SKU
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <ScanSearch className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Detect & Update Missing</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Only downloads images for products that don't have one yet. Existing images are kept.
            </p>
            <Button
              onClick={handleDetectImageSync}
              disabled={detectingImages || syncingImages || reversingImages}
              loading={detectingImages}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
            >
              <ScanSearch className="h-4 w-4 mr-2" />
              {detectingImages ? 'Detecting...' : 'Detect & Update Missing'}
            </Button>
          </div>

          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Replace All Images</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Points each variant (by SKU) at its own Shopify image, served straight from Shopify&apos;s CDN — one run fixes all colors and scales to thousands of products.
            </p>
            {!showImageSyncConfirm ? (
              <Button
                onClick={() => setShowImageSyncConfirm(true)}
                disabled={syncingImages || detectingImages || reversingImages}
                loading={syncingImages}
                variant="secondary"
                className="w-full border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                {syncingImages ? 'Syncing...' : 'Replace All Images'}
              </Button>
            ) : (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    This will repoint every product image to its matching Shopify CDN image.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowImageSyncConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleImageSync}
                    loading={syncingImages}
                    className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                  >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Confirm
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {!detectingImages && !syncingImages && !reversingImages && (
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              onClick={handleResetStuckImageSync}
              disabled={resettingStuckSync}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="If image sync is stuck or shows 'Failed to create image sync job', click to clear any orphaned jobs."
            >
              {resettingStuckSync ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {resettingStuckSync ? 'Resetting...' : 'Reset stuck image sync'}
            </button>
          </div>
        )}

        {(detectingImages || syncingImages) && imageJobProgress && (
          <div className={`mt-4 p-4 ${detectingImages ? 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800' : 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800'} border rounded-lg`}>
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className={`h-5 w-5 animate-spin ${detectingImages ? 'text-cyan-600 dark:text-cyan-400' : 'text-teal-600 dark:text-teal-400'} flex-shrink-0`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${detectingImages ? 'text-cyan-800 dark:text-cyan-300' : 'text-teal-800 dark:text-teal-300'}`}>
                  {detectingImages ? 'Detecting missing images...' : 'Replacing all images from Shopify...'}
                </p>
                <p className={`text-xs ${detectingImages ? 'text-cyan-600 dark:text-cyan-400' : 'text-teal-600 dark:text-teal-400'} mt-0.5`}>
                  {imageJobProgress.syncedCount} / {imageJobProgress.totalEstimate} products scanned
                  {(imageJobProgress.stats as Record<string, number>)?.updated > 0 && (
                    <> {'\u2022'} {(imageJobProgress.stats as Record<string, number>).updated} images {detectingImages ? 'added' : 'updated'}</>
                  )}
                  {detectingImages && (imageJobProgress.stats as Record<string, number>)?.already_has_image > 0 && (
                    <> {'\u2022'} {(imageJobProgress.stats as Record<string, number>).already_has_image} skipped</>
                  )}
                  {!detectingImages && (imageJobProgress.stats as Record<string, number>)?.skipped_unchanged > 0 && (
                    <> {'\u2022'} {(imageJobProgress.stats as Record<string, number>).skipped_unchanged} unchanged</>
                  )}
                  {(() => {
                    const elapsed = (Date.now() - imageJobProgress.startedAt) / 1000;
                    const pct = imageJobProgress.totalEstimate > 0 ? imageJobProgress.syncedCount / imageJobProgress.totalEstimate : 0;
                    if (pct > 0.05 && elapsed > 5) {
                      const remaining = Math.round((elapsed / pct) * (1 - pct));
                      if (remaining > 0) {
                        const mins = Math.floor(remaining / 60);
                        const secs = remaining % 60;
                        return <> {'\u2022'} ~{mins > 0 ? `${mins}m ` : ''}{secs}s remaining</>;
                      }
                    }
                    return null;
                  })()}
                </p>
              </div>
              <button
                onClick={handleCancelSync}
                className={`px-3 py-1.5 text-xs font-medium ${detectingImages ? 'text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700 hover:bg-cyan-50 dark:hover:bg-cyan-900/30' : 'text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/30'} bg-white dark:bg-gray-800 border rounded-md transition-colors`}
              >
                Cancel
              </button>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div
                className={`${detectingImages ? 'bg-cyan-500' : 'bg-teal-500'} h-2 rounded-full transition-all duration-500 ease-out`}
                style={{
                  width: imageJobProgress.totalEstimate > 0
                    ? `${Math.min(100, Math.round((imageJobProgress.syncedCount / imageJobProgress.totalEstimate) * 100))}%`
                    : '2%',
                }}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {imageJobProgress.totalEstimate > 0
                ? `${Math.min(100, Math.round((imageJobProgress.syncedCount / imageJobProgress.totalEstimate) * 100))}%`
                : 'Starting...'}
            </p>
          </div>
        )}

        {detectImageResult && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Detect & Update Complete
                </span>
              </div>
              <button
                onClick={() => setShowDetectImageDetails(!showDetectImageDetails)}
                className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
              >
                {showDetectImageDetails ? 'Hide' : 'Show'} details
                {showDetectImageDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Images Added</span>
                <p className="font-semibold text-green-600">{detectImageResult.updated}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Already Have Image</span>
                <p className="font-semibold text-blue-600">{detectImageResult.already_has_image}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">No Image in Shopify</span>
                <p className="font-semibold text-amber-600">{detectImageResult.no_image}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Not Matched</span>
                <p className="font-semibold text-gray-600 dark:text-gray-300">{detectImageResult.no_match}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Errors</span>
                <p className={`font-semibold ${detectImageResult.errors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {detectImageResult.errors}
                </p>
              </div>
            </div>

            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Scanned {detectImageResult.scanned} Shopify products across {detectImageResult.total_products} local products
              {detectImageResult.draft_skipped > 0 && ` (${detectImageResult.draft_skipped} drafts skipped)`}
            </div>

            {showDetectImageDetails && (
              <div className="mt-3 space-y-3">
                {(detectImageResult.matched_by_sku > 0 || detectImageResult.matched_by_sku_map > 0 || detectImageResult.matched_by_variant_gid > 0 || detectImageResult.matched_by_product_gid > 0 || detectImageResult.matched_by_map > 0 || detectImageResult.matched_by_reverse > 0) && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Match Breakdown</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div className="bg-white dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">By SKU</span>
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{detectImageResult.matched_by_sku}</p>
                      </div>
                      {detectImageResult.matched_by_sku_map > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded p-2 border border-blue-200 dark:border-blue-800">
                          <span className="text-blue-600 dark:text-blue-400 text-xs">By SKU Map</span>
                          <p className="font-semibold text-sm text-blue-700 dark:text-blue-300">{detectImageResult.matched_by_sku_map}</p>
                        </div>
                      )}
                      <div className="bg-white dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">By Variant ID</span>
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{detectImageResult.matched_by_variant_gid}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">By Product ID</span>
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{detectImageResult.matched_by_product_gid}</p>
                      </div>
                      <div className="bg-white dark:bg-gray-900 rounded p-2 border border-gray-100 dark:border-gray-700">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">By Inventory Map</span>
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{detectImageResult.matched_by_map}</p>
                      </div>
                      {detectImageResult.matched_by_reverse > 0 && (
                        <div className="bg-white dark:bg-gray-900 rounded p-2 border border-green-200 dark:border-green-800">
                          <span className="text-green-600 dark:text-green-400 text-xs">By Reverse Lookup</span>
                          <p className="font-semibold text-sm text-green-700 dark:text-green-300">{detectImageResult.matched_by_reverse}</p>
                        </div>
                      )}
                    </div>
                    {(detectImageResult.broken_url_fixed > 0 || detectImageResult.never_encountered > 0) && (
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {detectImageResult.broken_url_fixed > 0 && (
                          <div className="bg-white dark:bg-gray-900 rounded p-2 border border-amber-200 dark:border-amber-800">
                            <span className="text-amber-600 dark:text-amber-400 text-xs">Broken URLs Fixed</span>
                            <p className="font-semibold text-sm text-amber-700 dark:text-amber-300">{detectImageResult.broken_url_fixed}</p>
                          </div>
                        )}
                        {detectImageResult.never_encountered > 0 && (
                          <div className="bg-white dark:bg-gray-900 rounded p-2 border border-red-200 dark:border-red-800">
                            <span className="text-red-600 dark:text-red-400 text-xs">Never Matched</span>
                            <p className="font-semibold text-sm text-red-700 dark:text-red-300">{detectImageResult.never_encountered}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {detectImageResult.no_image_skus?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400 mb-1">
                      No Image on Shopify ({detectImageResult.no_image})
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      These products matched but Shopify returned no image. Check these products in your Shopify admin.
                    </p>
                    <div className="max-h-48 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-amber-200 dark:border-amber-800 p-2">
                      {detectImageResult.no_image_skus.map((sku, i) => (
                        <p key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5 font-mono">
                          {sku}
                        </p>
                      ))}
                      {detectImageResult.no_image > detectImageResult.no_image_skus.length && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 pt-1 italic">
                          ...and {detectImageResult.no_image - detectImageResult.no_image_skus.length} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {detectImageResult.error_details.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                      Error Details ({detectImageResult.error_details.length})
                    </p>
                    <div className="max-h-40 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                      {detectImageResult.error_details.map((detail, i) => (
                        <p key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {imageSyncResult && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Full Image Sync Complete
                </span>
              </div>
              {(imageSyncResult.errors > 0 || imageSyncResult.no_match > 0) && (
                <button
                  onClick={() => setShowImageSyncDetails(!showImageSyncDetails)}
                  className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                >
                  {showImageSyncDetails ? 'Hide' : 'Show'} details
                  {showImageSyncDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Images Synced</span>
                <p className="font-semibold text-green-600">{imageSyncResult.updated}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Variant-sourced</span>
                <p className="font-semibold text-teal-600">{imageSyncResult.variant_sourced}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Product fallback</span>
                <p className="font-semibold text-blue-600">{imageSyncResult.product_fallback}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">No Image in Shopify</span>
                <p className="font-semibold text-amber-600">{imageSyncResult.no_image}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Not Matched</span>
                <p className="font-semibold text-gray-600 dark:text-gray-300">{imageSyncResult.no_match}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Errors</span>
                <p className={`font-semibold ${imageSyncResult.errors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {imageSyncResult.errors}
                </p>
              </div>
            </div>

            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Scanned {imageSyncResult.scanned} Shopify products across {imageSyncResult.total_products} local products
              {imageSyncResult.draft_skipped > 0 && ` (${imageSyncResult.draft_skipped} drafts skipped)`}
            </div>

            {showImageSyncDetails && imageSyncResult.error_details.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                  Error Details ({imageSyncResult.error_details.length})
                </p>
                <div className="max-h-40 overflow-y-auto bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2">
                  {imageSyncResult.error_details.map((detail, i) => (
                    <p key={i} className="text-xs text-gray-600 dark:text-gray-400 py-0.5">
                      {detail}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 p-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ScanSearch className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Reverse Lookup (Optional)</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Queries Shopify individually for local products that have a Shopify Product ID but no image.
                Run this after the main sync to catch any remaining misses.
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleReverseLookup}
              disabled={reversingImages || detectingImages || syncingImages}
              loading={reversingImages}
              variant="secondary"
              className="ml-4 flex-shrink-0 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            >
              <ScanSearch className="h-4 w-4 mr-1" />
              {reversingImages ? 'Looking up...' : 'Run Reverse Lookup'}
            </Button>
          </div>

          {reversingImages && reverseProgress && (
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-4 w-4 animate-spin text-amber-600 dark:text-amber-400" />
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  {reverseProgress.syncedCount} / {reverseProgress.totalEstimate} products checked
                  {(reverseProgress.stats as Record<string, number>)?.updated > 0 && (
                    <> {'\u2022'} {(reverseProgress.stats as Record<string, number>).updated} images added</>
                  )}
                </span>
                <button
                  onClick={handleCancelSync}
                  className="ml-auto px-2 py-1 text-xs text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 rounded hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  Cancel
                </button>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-amber-500 h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: reverseProgress.totalEstimate > 0
                      ? `${Math.min(100, Math.round((reverseProgress.syncedCount / reverseProgress.totalEstimate) * 100))}%`
                      : '5%',
                  }}
                />
              </div>
            </div>
          )}

          {reverseResult && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Images Added</span>
                <p className="font-semibold text-green-600">{(reverseResult as Record<string, number>).updated || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">No Image on Shopify</span>
                <p className="font-semibold text-amber-600">{(reverseResult as Record<string, number>).no_image || 0}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded p-2">
                <span className="text-gray-500 dark:text-gray-400 text-xs">Not Found</span>
                <p className="font-semibold text-gray-600 dark:text-gray-300">{(reverseResult as Record<string, number>).never_encountered || 0}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ImageRetryPanel />
        <SkuMappingPanel />
      </div>

      <div className="mb-6">
        <MappingConflicts onResolved={() => fetchSettings()} />
      </div>

      <div className="mb-6 card p-6">
        <SyncStatusDashboard />
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
              <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Auto-Sync
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Bidirectional sync every 15 minutes (pull + push)
              </p>
            </div>
          </div>

          <button
            onClick={handleToggleAutoSync}
            disabled={updatingAutoSync}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              settings?.auto_sync_enabled
                ? 'bg-green-500'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                settings?.auto_sync_enabled ? 'translate-x-7' : 'translate-x-0.5'
              }`}
            />
            {updatingAutoSync && (
              <Loader2 className="absolute inset-0 m-auto h-4 w-4 animate-spin text-white" />
            )}
          </button>
        </div>
      </div>

      <div className="mt-6 card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
              <Download className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Shopify Inventory Webhook Pull
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xl">
                When enabled, editing product quantity in Shopify updates app stock via the
                inventory_levels/update webhook (~1 minute). When disabled, Shopify quantity
                changes do not change your app database.
              </p>
            </div>
          </div>

          <button
            onClick={handleToggleInventoryPull}
            disabled={updatingInventoryPull}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              settings?.inventory_pull_enabled
                ? 'bg-green-500'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                settings?.inventory_pull_enabled ? 'translate-x-7' : 'translate-x-0.5'
              }`}
            />
            {updatingInventoryPull && (
              <Loader2 className="absolute inset-0 m-auto h-4 w-4 animate-spin text-white" />
            )}
          </button>
        </div>
      </div>

      <div className="mt-6 card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Sync Status
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Last Pull</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {formatRelativeTime(settings?.last_pull_at || null)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Last Push</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {formatRelativeTime(settings?.last_push_at || null)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <Link
          to="/shopify"
          className="text-sm text-cyan-600 hover:text-cyan-700 inline-flex items-center gap-1"
        >
          <Settings className="h-4 w-4" />
          Manage Shopify Connection
        </Link>
      </div>

      <ImportPreviewModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        products={previewProducts}
        existingCategories={existingCategories}
        existingLocations={existingLocations}
        onImport={handleImportProducts}
        loading={previewLoading}
        loadingProgress={loadingProgress}
        stats={previewStats}
        ambiguousMatches={ambiguousMatches}
      />
    </div>
  );
};
